import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../common/ConfirmDialog';

interface LocationGrant {
  organization_id: number;
  location_id: number;
  granted_by: string | null;
  granted_at: string;
  location_icao_code: string | null;
  location_name: string | null;
  location_country_code: string | null;
}

interface LocationOption {
  id: number;
  icao_code: string;
  name: string;
  country_code: string | null;
}

interface OrganizationLocationsTabProps {
  organizationId: number;
}

export default function OrganizationLocationsTab({ organizationId }: OrganizationLocationsTabProps) {
  const [grants, setGrants] = useState<LocationGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState<LocationGrant | null>(null);

  // Add dialog state
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    loadGrants();
  }, [organizationId]);

  const loadGrants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<LocationGrant[]>(
        `/admin/organizations/${organizationId}/locations`
      );
      setGrants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load location grants');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = async () => {
    setAddDialogOpen(true);
    setSelectedLocation(null);
    setAddError(null);

    try {
      setLoadingLocations(true);
      const allLocations = await apiClient.get<LocationOption[]>('/admin/locations');
      const grantedIds = new Set(grants.map((g) => g.location_id));
      const available = allLocations.filter((loc: LocationOption) => !grantedIds.has(loc.id));
      setLocations(available);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleAddGrant = async () => {
    if (!selectedLocation) return;

    try {
      setAddLoading(true);
      setAddError(null);
      await apiClient.post(`/admin/organizations/${organizationId}/locations`, {
        location_id: selectedLocation.id,
      });
      setAddDialogOpen(false);
      await loadGrants();
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'Failed to grant location access'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveGrant = (grant: LocationGrant) => {
    setSelectedGrant(grant);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedGrant) return;

    try {
      await apiClient.delete(
        `/admin/organizations/${organizationId}/locations/${selectedGrant.location_id}`
      );
      setRemoveDialogOpen(false);
      setSelectedGrant(null);
      await loadGrants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke location access');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Location Access Grants</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} size="small">
          Grant Location
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ICAO Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Date Granted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grants.map((grant) => (
              <TableRow key={grant.location_id}>
                <TableCell>{grant.location_icao_code || '-'}</TableCell>
                <TableCell>{grant.location_name || '-'}</TableCell>
                <TableCell>{grant.location_country_code || '-'}</TableCell>
                <TableCell>{formatDate(grant.granted_at)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveGrant(grant)}
                    title="Revoke access"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {grants.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No location grants yet
          </Typography>
        </Box>
      )}

      {/* Grant Location Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Location Access</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            {loadingLocations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => `${option.icao_code} - ${option.name}`}
                value={selectedLocation}
                onChange={(_, newValue) => setSelectedLocation(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Location" placeholder="Search locations..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGrant}
            variant="contained"
            disabled={addLoading || !selectedLocation}
          >
            {addLoading ? 'Granting...' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={removeDialogOpen}
        title="Revoke Location Access"
        message={`Are you sure you want to revoke access to "${selectedGrant?.location_icao_code} - ${selectedGrant?.location_name}" from this organisation?`}
        confirmText="Revoke"
        confirmColor="error"
        onConfirm={handleConfirmRemove}
        onCancel={() => {
          setRemoveDialogOpen(false);
          setSelectedGrant(null);
        }}
      />
    </Box>
  );
}
